'use client';

import React, { useEffect, useState, useRef } from 'react';
import styles from './scss/segmentation.module.scss';
import ApexBarChart from '../../components/custom_components/apexChartsTypes/barChart';
import ApexLineChart from '../../components/custom_components/apexChartsTypes/lineChart';
import { getLycaSegementationDistributionChart } from '../../apiservice/aiLycaPilot';

/* -------------------- Typing Effect -------------------- */
const TypingText = ({
  text,
  onDone,
  speed = 50,
}: {
  text: string;
  onDone?: () => void;
  speed?: number;
}) => {
  const [displayedText, setDisplayedText] = useState('');

  useEffect(() => {
    let i = 0;
    setDisplayedText('');
    const interval = setInterval(() => {
      setDisplayedText(text.slice(0, i + 1));
      i++;
      if (i === text.length) {
        clearInterval(interval);
        onDone?.();
      }
    }, speed);

    return () => clearInterval(interval);
  }, [text, speed, onDone]);

  return <>{displayedText}</>;
};

/* -------------------- Main Component -------------------- */
const SegMent3 = ({ onComplete }: { onComplete?: () => void }) => {
  const [seriesSegment, setSeriesSegment] = useState<any[]>([]);
  const [categoriessegment, setCategoriesSegment] = useState<any[]>([]);
  const [phase, setPhase] = useState<
    'title' | 'desc' | 'caption' | 'footer' | 'done'
  >('title');

  // Refs for scrolling into view
  const titleRef = useRef<HTMLParagraphElement>(null);
  const descRef = useRef<HTMLSpanElement>(null);
  const captionRef = useRef<HTMLParagraphElement>(null);
  const footerRef = useRef<HTMLParagraphElement>(null);

  /* -------- Fetch API Data -------- */
  useEffect(() => {
    const getLycaSegementationDistributionChartData = async () => {
      try {
        const body = { fromYear: '2024', toYear: '2025', domainId: ' ' };
        const res = await getLycaSegementationDistributionChart(body);

        if (res?.data?.statusCode === 200) {
          const data = res.data.response;

          // Build chart categories & series
          const categories = data.map(
            (item: any) => `Segment ${item.Segments}`
          );
          const chartData = data.map((item: any) => item.TotalRevenue || 0);

          setSeriesSegment([
            {
              name: 'Total Revenue',
              data: chartData,
            },
          ]);
          setCategoriesSegment(categories);
        }
      } catch (error) {
        console.error('Error fetching chart data:', error);
      }
    };

    getLycaSegementationDistributionChartData();
  }, []);

  /* -------- Scroll into current phase -------- */
  useEffect(() => {
    let el: HTMLElement | null = null;
    if (phase === 'title') el = titleRef.current;
    else if (phase === 'desc') el = descRef.current;
    else if (phase === 'caption') el = captionRef.current;
    else if (phase === 'footer') el = footerRef.current;

    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [phase]);

  /* -------- Phase Handler -------- */
  const handleDone = () => {
    if (phase === 'title') setPhase('desc');
    else if (phase === 'desc') setPhase('caption');
    else if (phase === 'caption') setPhase('footer');
    else if (phase === 'footer') {
      setPhase('done');
      onComplete?.();
    }
  };

  /* -------- Chart Section Data -------- */
  const current = {
    title: 'Segment Distribution Chart',
    desc: 'Track the revenue for top-ups and Bundle from the segments',
    chart: 'bar',
    caption: 'x-axis Segments   y-axis Total revenue',
    footer:
      'Focus on years with high growth to replicate strategies and investigate low-performing years to uncover issues.',
    color: '#575CFF',
    series: seriesSegment,
    categories: categoriessegment,
  };

  /* -------- Render -------- */
  return (
    
    <div className={styles.clvchartssec}>
      <div className={`${styles.clvchartwrapper} revealing`}>
        {/* HEADER */}
        <div className={styles.clvwrapheader}>
          <p className={styles.clvwrapheadertxt} ref={titleRef}>
            {phase === 'title' ? (
              <TypingText text={current.title} onDone={handleDone} />
            ) : (
              current.title
            )}
          </p>
          <span className={styles.clvwrpadesc} ref={descRef}>
            {phase === 'desc' && (
              <TypingText text={current.desc} onDone={handleDone} />
            )}
            {phase !== 'title' && phase !== 'desc' && current.desc}
          </span>
        </div>

        {/* BODY */}
        {['caption', 'footer', 'done'].includes(phase) && (
          <div className={styles.clvwrapbody}>
            <p className={styles.clvwrapcaptions} ref={captionRef}>
              {phase === 'caption' ? (
                <TypingText text={current.caption} onDone={handleDone} />
              ) : (
                current.caption
              )}
            </p>

            {current.chart === 'bar' ? (
              <ApexBarChart
                isStacked={false}
                series={current.series}
                colors={[current.color]}
                categories={current.categories}
              />
            ) : (
              <ApexLineChart
                series={[{ data: [10, 41, 35, 51, 49] }]}
                categories={['2019', '2021', '2023', '2025', '2027']}
              />
            )}
          </div>
        )}

        {/* FOOTER */}
        {['footer', 'done'].includes(phase) && (
          <div className={styles.clvwrapfooter}>
            <span className="insightsbtn">AI Insights</span>
            <p className={styles.clvwrapfttxt} ref={footerRef}>
              {phase === 'footer' ? (
                <TypingText text={current.footer} onDone={handleDone} />
              ) : (
                current.footer
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SegMent3;
